<template>
  <section class="container py-5">
    <div v-if="loading">Loading...</div>
    <div v-else-if="user">
      <div class="row">
        <div class="col-lg-10 col-xl-8">
          <h5 class="mb-3">Account</h5>
          <div class="row align-items-center mb-2">
            <strong class="col-md-4 col-lg-3">E-mail</strong>
            <div class="col-md-5 col-lg-6 text-break">
              {{ user.email }}
              <span class="text-success" v-if="user.emailVerified">(verified)</span>
              <span class="text-muted" v-else>(not verified)</span>
            </div>
            <div class="col-md-3 col-lg-3">
              <button class="btn btn-outline-secondary btn-sm" @click="showDeleteAccount">Delete account</button>
            </div>
          </div>
          <div class="row mb-2" v-if="user.displayName">
            <strong class="col-md-4 col-lg-3">Name</strong>
            <div class="col-md-8 col-lg-9 text-break">{{ user.displayName }}</div>
          </div>

          <h5 class="mt-5 mb-3">Multi-factor authentication</h5>
          <template v-if="isSMSFactorEnabled">
            <div class="row align-items-center mb-2">
              <strong class="col-md-4 col-lg-3">SMS</strong>
              <div class="col-md-5 col-lg-6 text-break">
                <template v-if="isSMSFactorEnabled">
                  {{ getPhoneNumber() }}
                  <span class="text-success">(enabled)</span>
                </template>
                <span class="text-muted" v-else>not enabled</span>
              </div>
              <div class="col-md-3 col-lg-3">
                <button class="btn btn-primary btn-sm" v-if="!isSMSFactorEnabled && !showSMSEnrollment" @click="toggleSMSEnrollment">Enable</button>
              </div>
            </div>
            <div class="row mb-3" v-if="showSMSEnrollment">
              <div class="col-md-8 offset-md-4 col-lg-9 offset-lg-3">
                <SMSEnrollment></SMSEnrollment>
              </div>
            </div>
          </template>
          <div class="row align-items-center mb-2">
            <strong class="col-md-4 col-lg-3">Authenticator app</strong>
            <div class="col-md-5 col-lg-6 text-break">
              <span class="text-success" v-if="isTOTPFactorEnabled">(enabled)</span>
              <span class="text-muted" v-else>not enabled</span>
            </div>
            <div class="col-md-3 col-lg-3">
              <button class="btn btn-primary btn-sm" v-if="!isTOTPFactorEnabled && !showTOTPEnrollment" @click="toggleTOTPEnrollment">Enable</button>
            </div>
          </div>
          <div class="row mb-3" v-if="showTOTPEnrollment">
            <div class="col-md-8 offset-md-4 col-lg-9 offset-lg-3">
              <TOTPEnrollment></TOTPEnrollment>
            </div>
          </div>
        </div>
      </div>
      <div v-if="subscriptionEnabled" class="mt-5">
        <h5 class="mb-3">Email notifications for ASPSP status changes</h5>
        <div v-if="brokerOrigins.length > 1" class="form-inline mb-3">
          <select v-model="brokerOrigin" name="brokerOrigin" id="brokerOrigin" class="form-control">
            <option v-for="origin in brokerOrigins" :key="origin.value" :value="origin.value">
              {{ origin.name }}
            </option>
          </select>
        </div>
        <div v-if="Object.keys(subscriptions).length">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th class="align-middle">Country</th>
              <th class="align-middle">PSU Type</th>
              <th class="align-middle">Service</th>
              <th class="align-middle">
                Unsubscribe
                  <label class="form-check-label ml-1">
                    <input
                      type="checkbox"
                      @change="toggleUnsubscribeAll($event.target.checked)"
                      :checked="Object.keys(subscriptions).every(country => selectedSubscriptionIds[country])"
                    />
                    <small> Select All</small>
                  </label>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="country in Object.keys(subscriptions).sort((a, b) => countriesList[a].localeCompare(countriesList[b]))" :key="`country-${country}`">
              <td>{{ countriesList[country] }}</td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].psu_types.includes('personal')"
                    @change="togglePsuType(country, 'personal', $event.target.checked)"
                  />
                  Personal
                  </label>
                </div>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].psu_types.includes('business')"
                    @change="togglePsuType(country, 'business', $event.target.checked)"
                  />
                  Business
                  </label>
                </div>
              </td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].services.includes('AIS')"
                    @change="toggleService(country, 'AIS', $event.target.checked)"
                  />
                  AIS
                  </label>
                </div>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].services.includes('PIS')"
                    @change="toggleService(country, 'PIS', $event.target.checked)"
                  />
                  PIS
                  </label>
                </div>
              </td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="selectedSubscriptionIds[country]"
                    @change="toggleUnsubscribe(country, subscriptionIds[country], $event.target.checked)"
                  />
                  Unsubscribe
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mt-3">
          <button class="btn btn-primary" @click="updateAndUnsubscribe">Update Subscriptions</button>
        </div>
        <p class="mt-3">You can subscribe to more countries by visiting <a href="/cp/aspsps">ASPSP status</a> page</p>
      </div>
      </div>
      <b-modal
        ref="deleteAccountModalRef"
        id="delete-account"
        title="Delete account"
        hide-footer
        @hidden="resetDeleteAccount"
      >
        <p>
          Deleting the account <strong>{{ user.email }}</strong> permanently removes your account data (including email address, full name, phone number, Mock ASPSP data,
           and identifiers of accounts linked to production applications), deactivates your applications, and cancels your subscriptions to ASPSP availability notifications. 
          <strong>This cannot be undone.</strong>
        </p>
        <div class="form-group">
          <label for="deleteAccountEmail">
            Type <strong>{{ user.email }}</strong> to confirm
          </label>
          <input
            id="deleteAccountEmail"
            v-model="deleteAccountEmail"
            type="email"
            class="form-control"
            autocomplete="off"
            :disabled="deletingAccount"
            @keyup.enter="deleteAccount"
          />
          <small class="form-text text-muted" v-if="deleteAccountEmail && !isDeleteAccountConfirmed">
            The e-mail does not match the account e-mail.
          </small>
        </div>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-secondary mr-2"
            :disabled="deletingAccount"
            @click="$refs.deleteAccountModalRef.hide()"
          >
            Cancel
          </button>
          <button
            class="btn btn-danger"
            :disabled="!isDeleteAccountConfirmed || deletingAccount"
            @click="deleteAccount"
          >
            {{ deletingAccount ? 'Deleting...' : 'Delete account' }}
          </button>
        </div>
      </b-modal>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import metadata from '../mixins/metadata.js'
import SMSEnrollment from '../components/SMSEnrollment.vue'
import TOTPEnrollment from '../components/TOTPEnrollment.vue'
import { BModal } from 'bootstrap-vue/src/components/modal'
import axios from 'axios'

export default {
  name: 'UserProfile',
  mixins: [metadata],
  data() {
    return {
      showSMSEnrollment: false,
      showTOTPEnrollment: false,
      deleteAccountEmail: '',
      deletingAccount: false,
      loadingASPSPs: false,
      countries: null,
      country: '',
      subscriptions: {},
      isSubscribed: {},
      subscriptionIds: {},
      selectedSubscriptionIds: {},
      brokerOrigin: localStorage.getItem('selected_broker') || ''
    }
  },
  watch: {
    brokerOrigin: {
      handler(brokerOrigin) {
        localStorage.setItem('selected_broker', brokerOrigin);
        (async () => {
          await this.getUserSubscriptions(brokerOrigin)
        })()
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['user', 'userProfile', 'loading', 'idToken', 'billingAccounts']),
    ...mapState({
      countriesList: (state) => state.countriesList,
      brokerOriginsNameMap: (state) => state.brokerOriginsNameMap
    }),
    selectedBroker() {
      // get selected broker from local storage
      return localStorage.getItem('selected_broker') || ''
    },
    brokerOrigins() {
      if (!this.userProfile) return []
      const userOrigins = this.userProfile.brokerOrigins
      if (!userOrigins || !userOrigins.length) {
        return []
      }
      const defaultBrokerOrigin = ''
      const allowedOrigins = [
        {
          name: 'Default broker',
          value: defaultBrokerOrigin,
          selected: defaultBrokerOrigin === this.selectedBroker
        }
      ]
      if (this.selectedBroker && !userOrigins.includes(this.selectedBroker)) {
        // If the user lost access to the selected broker, reset the selection
        localStorage.setItem('selected_broker', '')
      }
      userOrigins.forEach((origin) => {
        if (this.brokerOriginsNameMap[origin]) {
          allowedOrigins.push({
            name: this.brokerOriginsNameMap[origin],
            value: origin,
            selected: origin === this.selectedBroker
          })
        }
      })
      return allowedOrigins
    },
    isTOTPFactorEnabled() {
      const { enrolledFactors } = (this.user && this.user.multiFactor) || {}
      return enrolledFactors && enrolledFactors.some(factor => factor.factorId === 'totp')
    },
    isSMSFactorEnabled() {
      const { enrolledFactors } = (this.user && this.user.multiFactor) || {}
      return enrolledFactors && enrolledFactors.some(factor => factor.factorId === 'phone')
    },
    hasBillingAccount() {
      return !!(this.billingAccounts && this.billingAccounts.length > 0)
    },
    isDeleteAccountConfirmed() {
      const email = this.user && this.user.email
      return !!email && this.deleteAccountEmail.trim().toLowerCase() === email.toLowerCase()
    },
    subscriptionEnabled() {
      return true
      // return this.idToken && this.idToken.claims && this.idToken.claims.enabledFeatures && this.idToken.claims.enabledFeatures.includes('aspspSubscriptions')
    }
  },
  components: {
    SMSEnrollment,
    TOTPEnrollment,
    BModal
  },
  methods: {
    ...mapActions(['pushAlertMessage', 'getSubscriptions', 'unsubscribe', 'deleteUser', 'logout']),
    showDeleteAccount() {
      this.$refs.deleteAccountModalRef.show()
    },
    resetDeleteAccount() {
      this.deleteAccountEmail = ''
    },
    async deleteAccount() {
      if (!this.isDeleteAccountConfirmed || this.deletingAccount) {
        return
      }
      this.deletingAccount = true
      try {
        if (!(await this.deleteUser())) {
          return
        }
        this.$refs.deleteAccountModalRef.hide()
        this.pushAlertMessage({ message: 'Your account has been deleted', type: 'info' })
        await this.logout()
        this.$router.push({ name: 'sign-in' })
      } finally {
        this.deletingAccount = false
      }
    },
    toggleTOTPEnrollment() {
      this.showTOTPEnrollment = !this.showTOTPEnrollment
    },
    toggleSMSEnrollment() {
      this.showSMSEnrollment = !this.showSMSEnrollment
    },
    getPhoneNumber() {
      const { enrolledFactors } = (this.user && this.user.multiFactor) || {}
      const phoneFactor = (enrolledFactors || []).find(factor => factor.factorId === 'phone')
      return phoneFactor ? phoneFactor.phoneNumber : ''
    },
    async getUserSubscriptions(broker) {
      const payload = {}
      if (broker) {
        payload.broker = broker
      }
      const subscriptionsResponse = await this.getSubscriptions(payload)
      const allSubscriptions = {}
      const subscriptions = subscriptionsResponse.data.subscriptions
      for (const subscription of subscriptions) {
        // Check if the country already exists in allSubscriptions
        if (!(subscription.country in allSubscriptions)) {
          allSubscriptions[subscription.country] = {}
        }
        allSubscriptions[subscription.country] = subscription
      }
      this.subscriptions = allSubscriptions
      console.log('subscriptions:', this.subscriptions)
      // Initialize isSubscribed and subscriptionIds for each country
      for (const country in this.countriesList) {
        // Find the subscription for the current country
        const subscription = subscriptions.find(
          (sub) => sub.country === country
        )
        // Set isSubscribed[country] to true if a subscription exists,otherwise false
        this.$set(this.isSubscribed, country, !!subscription)

        // Store the subscription ID if a subscription exists
        this.$set(this.subscriptionIds, country, subscription ? subscription.kid : null)
      }
    },
    togglePsuType(country, type, isChecked) {
      if (!this.subscriptions[country]) {
        return
      }
      const psuTypes = this.subscriptions[country].psu_types || []
      if (isChecked) {
        // Add the type if it's not already in the array
        if (!psuTypes.includes(type)) {
          psuTypes.push(type)
        }
        // Uncheck the "Unsubscribe" checkbox
        this.$delete(this.selectedSubscriptionIds, country)
      } else {
        // Remove the type if it's in the array
        const index = psuTypes.indexOf(type)
        if (index > -1) {
          psuTypes.splice(index, 1)
        }
      }
      this.$set(this.subscriptions[country], 'psu_types', [...psuTypes])
    },
    toggleService(country, service, isChecked) {
      if (!this.subscriptions[country]) {
        return
      }
      const services = this.subscriptions[country].services || []
      if (isChecked) {
        // Add the service if it's not already in the array
        if (!services.includes(service)) {
          services.push(service)
        }
        // Uncheck the "Unsubscribe" checkbox
        this.$delete(this.selectedSubscriptionIds, country)
      } else {
        // Remove the service if it's in the array
        const index = services.indexOf(service)
        if (index > -1) {
          services.splice(index, 1)
        }
      }
      this.$set(this.subscriptions[country], 'services', [...services])
    },
    toggleUnsubscribe(country, subscriptionId, isChecked) {
      if (isChecked) {
        // Add the subscription ID to selectedSubscriptionIds
        this.$set(this.selectedSubscriptionIds, country, subscriptionId)

        // Clear all PSU types and services for the country
        if (this.subscriptions[country]) {
          this.$set(this.subscriptions[country], 'psu_types', [])
          this.$set(this.subscriptions[country], 'services', [])
        }
      } else {
        // Remove the subscription ID from selectedSubscriptionIds
        this.$delete(this.selectedSubscriptionIds, country)
      }
    },
    async updateAndUnsubscribe() {
      try {
        // Handle unsubscriptions
        if (Object.keys(this.selectedSubscriptionIds).length > 0) {
          for (const [country, subscriptionId] of Object.entries(this.selectedSubscriptionIds)) {
            if (!subscriptionId) {
              continue // Skip if no subscription ID is provided
            }
            const payload = {
              subscriptionId: subscriptionId
            }
            const response = await this.unsubscribe(payload)
            this.$delete(this.subscriptions, country)
            console.log('Unsubscribe response:', response.data)
          }
        }
        // Update subscriptions
        const payload = {
          subscriptions: this.subscriptions
        }
        const response = await axios.patch('/api/subscriptions', payload, {
          headers: {
            Authorization: `Bearer ${this.idToken.token}`
          }
        })
        this.pushAlertMessage({ message: 'Subscriptions updated successfully!', type: 'info' })
        console.log('Update response:', response.data)
      } catch (error) {
        console.error('Error during update and unsubscribe:', error)
        this.pushAlertMessage({ message: 'Failed to update subscriptions or unsubscribe.', type: 'error' })
      }
    },
    toggleUnsubscribeAll(isChecked) {
      Object.keys(this.subscriptions).forEach((country) => {
        if (isChecked) {
          // Add all countries to selectedSubscriptionIds
          this.$set(this.selectedSubscriptionIds, country, this.subscriptionIds[country])

          // Clear all PSU types and services for the country
          if (this.subscriptions[country]) {
            this.$set(this.subscriptions[country], 'psu_types', [])
            this.$set(this.subscriptions[country], 'services', [])
          }
        } else {
          // Remove all countries from selectedSubscriptionIds
          this.$delete(this.selectedSubscriptionIds, country)
        }
      })
    }
  }
}
</script>
